import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    useToast,
    Heading,
    Card,
    CardHeader,
    CardBody,
    Divider,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Spacer,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Text,
    Badge,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import {
    useCertifications,
    useCreateCertification,
    useUpdateCertification,
    useDeleteCertification,
    useInsurance,
    useCreateInsurance,
    useUpdateInsurance,
    useDeleteInsurance,
} from '../../services/contractor-profile.hooks';

export function CertificationsTab() {
    const toast = useToast();

    // Certs Hooks
    const { data: certs, isLoading: isLoadingCerts } = useCertifications();
    const createCert = useCreateCertification();
    const updateCert = useUpdateCertification();
    const deleteCert = useDeleteCertification();

    // Insurance Hooks
    const { data: insurance, isLoading: isLoadingIns } = useInsurance();
    const createIns = useCreateInsurance();
    const updateIns = useUpdateInsurance();
    const deleteIns = useDeleteInsurance();

    // Modal state
    const { isOpen: isCertOpen, onOpen: onCertOpen, onClose: onCertClose } = useDisclosure();
    const { isOpen: isInsOpen, onOpen: onInsOpen, onClose: onInsClose } = useDisclosure();

    const [editingCertId, setEditingCertId] = useState<number | null>(null);
    const [editingInsId, setEditingInsId] = useState<number | null>(null);

    const [certForm, setCertForm] = useState({
        cert_type: '',
        cert_number: '',
        issuing_body: '',
        expiration_date: '',
        scan_url: '',
    });

    const [insForm, setInsForm] = useState({
        policy_type: '',
        carrier: '',
        limits: '',
        expiration_date: '',
        certificate_url: '',
    });

    // --- Formatters and Helpers ---
    const isExpired = (dateString: string) => {
        if (!dateString) return false;
        return new Date(dateString) < new Date();
    };

    const isExpiringSoon = (dateString: string) => {
        if (!dateString) return false;
        const now = new Date();
        const exp = new Date(dateString);
        const diffDays = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 3600 * 24));
        return diffDays > 0 && diffDays <= 45; // Expiring within 45 days
    };

    const StatusBadge = ({ dateString }: { dateString: string }) => {
        if (!dateString) return null;
        if (isExpired(dateString)) return <Badge colorScheme="red">Expired</Badge>;
        if (isExpiringSoon(dateString)) return <Badge colorScheme="orange">Expiring Soon</Badge>;
        return <Badge colorScheme="green">Active</Badge>;
    };

    // --- Certifications Handlers ---
    const handleOpenCert = (item?: any) => {
        if (item) {
            setEditingCertId(item.id);
            setCertForm({
                cert_type: item.cert_type || '',
                cert_number: item.cert_number || '',
                issuing_body: item.issuing_body || '',
                expiration_date: item.expiration_date ? new Date(item.expiration_date).toISOString().split('T')[0] : '',
                scan_url: item.scan_url || '',
            });
        } else {
            setEditingCertId(null);
            setCertForm({ cert_type: '', cert_number: '', issuing_body: '', expiration_date: '', scan_url: '' });
        }
        onCertOpen();
    };

    const handleSaveCert = () => {
        const payload = {
            ...certForm,
            expiration_date: certForm.expiration_date ? new Date(certForm.expiration_date).toISOString() : null,
        };
        if (editingCertId) {
            updateCert.mutate({ id: editingCertId, data: payload }, {
                onSuccess: () => { toast({ title: 'Updated successfully', status: 'success' }); onCertClose(); }
            });
        } else {
            createCert.mutate(payload, {
                onSuccess: () => { toast({ title: 'Added successfully', status: 'success' }); onCertClose(); }
            });
        }
    };

    // --- Insurance Handlers ---
    const handleOpenIns = (item?: any) => {
        if (item) {
            setEditingInsId(item.id);
            setInsForm({
                policy_type: item.policy_type || '',
                carrier: item.carrier || '',
                limits: item.limits || '',
                expiration_date: item.expiration_date ? new Date(item.expiration_date).toISOString().split('T')[0] : '',
                certificate_url: item.certificate_url || '',
            });
        } else {
            setEditingInsId(null);
            setInsForm({ policy_type: '', carrier: '', limits: '', expiration_date: '', certificate_url: '' });
        }
        onInsOpen();
    };

    const handleSaveIns = () => {
        const payload = {
            ...insForm,
            expiration_date: insForm.expiration_date ? new Date(insForm.expiration_date).toISOString() : null,
        };
        if (editingInsId) {
            updateIns.mutate({ id: editingInsId, data: payload }, {
                onSuccess: () => { toast({ title: 'Updated successfully', status: 'success' }); onInsClose(); }
            });
        } else {
            createIns.mutate(payload, {
                onSuccess: () => { toast({ title: 'Added successfully', status: 'success' }); onInsClose(); }
            });
        }
    };

    if (isLoadingCerts || isLoadingIns) return <Box>Loading...</Box>;

    return (
        <VStack spacing={8} align="stretch" w="full">
            {/* Certifications Card */}
            <Card>
                <CardHeader>
                    <HStack>
                        <Heading size="md">Business Certifications (MBE, WBE, DBE)</Heading>
                        <Spacer />
                        <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm" onClick={() => handleOpenCert()}>
                            Add Certification
                        </Button>
                    </HStack>
                </CardHeader>
                <Divider color="gray.200" />
                <CardBody>
                    {certs?.length === 0 ? (
                        <Text color="gray.500">No certifications recorded.</Text>
                    ) : (
                        <Table variant="simple" size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Type</Th>
                                    <Th>Number</Th>
                                    <Th>Issuing Body</Th>
                                    <Th>Expiration</Th>
                                    <Th>Status</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {certs?.map((item: any) => (
                                    <Tr key={item.id}>
                                        <Td fontWeight="medium">{item.cert_type}</Td>
                                        <Td>{item.cert_number}</Td>
                                        <Td>{item.issuing_body}</Td>
                                        <Td>{item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : 'N/A'}</Td>
                                        <Td><StatusBadge dateString={item.expiration_date} /></Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <IconButton aria-label="Edit" icon={<MdEdit />} size="xs" variant="ghost" onClick={() => handleOpenCert(item)} />
                                                <IconButton aria-label="Delete" icon={<MdDelete />} size="xs" colorScheme="red" variant="ghost" onClick={() => {
                                                    if (confirm('Delete this cert?')) deleteCert.mutate(item.id);
                                                }} />
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Insurance Card */}
            <Card>
                <CardHeader>
                    <HStack>
                        <Heading size="md">Insurance & Bonding</Heading>
                        <Spacer />
                        <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm" onClick={() => handleOpenIns()}>
                            Add Insurance
                        </Button>
                    </HStack>
                </CardHeader>
                <Divider color="gray.200" />
                <CardBody>
                    {insurance?.length === 0 ? (
                        <Text color="gray.500">No insurance policies recorded.</Text>
                    ) : (
                        <Table variant="simple" size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Policy Type</Th>
                                    <Th>Carrier</Th>
                                    <Th>Limits</Th>
                                    <Th>Expiration</Th>
                                    <Th>Status</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {insurance?.map((item: any) => (
                                    <Tr key={item.id}>
                                        <Td fontWeight="medium">{item.policy_type}</Td>
                                        <Td>{item.carrier}</Td>
                                        <Td>{item.limits}</Td>
                                        <Td>{item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : 'N/A'}</Td>
                                        <Td><StatusBadge dateString={item.expiration_date} /></Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <IconButton aria-label="Edit" icon={<MdEdit />} size="xs" variant="ghost" onClick={() => handleOpenIns(item)} />
                                                <IconButton aria-label="Delete" icon={<MdDelete />} size="xs" colorScheme="red" variant="ghost" onClick={() => {
                                                    if (confirm('Delete this policy?')) deleteIns.mutate(item.id);
                                                }} />
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Cert Modal */}
            <Modal isOpen={isCertOpen} onClose={onCertClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingCertId ? 'Edit Certification' : 'Add Certification'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Certification Type</FormLabel>
                                <Input value={certForm.cert_type} onChange={e => setCertForm(p => ({ ...p, cert_type: e.target.value }))} placeholder="MBE, WBE, DBE, etc." />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Certification Number</FormLabel>
                                <Input value={certForm.cert_number} onChange={e => setCertForm(p => ({ ...p, cert_number: e.target.value }))} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Issuing Body</FormLabel>
                                <Input value={certForm.issuing_body} onChange={e => setCertForm(p => ({ ...p, issuing_body: e.target.value }))} placeholder="City of Chicago, State of IL, etc." />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Expiration Date</FormLabel>
                                <Input type="date" value={certForm.expiration_date} onChange={e => setCertForm(p => ({ ...p, expiration_date: e.target.value }))} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>File / Scan URL</FormLabel>
                                <Input value={certForm.scan_url} onChange={e => setCertForm(p => ({ ...p, scan_url: e.target.value }))} />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onCertClose}>Cancel</Button>
                        <Button colorScheme="brand" onClick={handleSaveCert} isLoading={createCert.isPending || updateCert.isPending}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Insurance Modal */}
            <Modal isOpen={isInsOpen} onClose={onInsClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingInsId ? 'Edit Insurance' : 'Add Insurance'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Policy Type</FormLabel>
                                <Input value={insForm.policy_type} onChange={e => setInsForm(p => ({ ...p, policy_type: e.target.value }))} placeholder="General Liability, Workers Comp, etc." />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Carrier</FormLabel>
                                <Input value={insForm.carrier} onChange={e => setInsForm(p => ({ ...p, carrier: e.target.value }))} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Limits</FormLabel>
                                <Input value={insForm.limits} onChange={e => setInsForm(p => ({ ...p, limits: e.target.value }))} placeholder="e.g. $1M / $2M" />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Expiration Date</FormLabel>
                                <Input type="date" value={insForm.expiration_date} onChange={e => setInsForm(p => ({ ...p, expiration_date: e.target.value }))} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Certificate URL</FormLabel>
                                <Input value={insForm.certificate_url} onChange={e => setInsForm(p => ({ ...p, certificate_url: e.target.value }))} />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onInsClose}>Cancel</Button>
                        <Button colorScheme="brand" onClick={handleSaveIns} isLoading={createIns.isPending || updateIns.isPending}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
}
